import { useState } from 'react';
import {
  Card,
  CardTitle,
  CohortHeatmap,
  EmptyState,
  ErrorState,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableRowHeader,
  TableSkeleton,
  formatNumber,
} from '@mgdlead/ui';
import { CalendarDays, Clock, Users } from 'lucide-react';

import { PageHeader } from '@mgdlead/ui';
import { FormAlert } from '@mgdlead/ui';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';
import {
  useAnalyticsActivity,
  useAnalyticsCohorts,
  useAnalyticsHourly,
  type AnalyticsCohorts,
} from '../../../lib/api';
import { PeriodFilter } from './AnalyticsShell';
import { EvasaoPorOrigem } from '../../../features/retention/RetentionTables';
import { ActivityGrid, ChartCard, ColumnChart, RampLegend } from './charts';
import { SERIE, diaLongo, horaCurta, type PeriodoId } from './analytics-ui';

/**
 * Histórico — a linha do tempo: hora do dia, atividade do ano e retenção por coorte.
 *
 * As três leituras que **não cabem no painel diário** porque respondem em outra cadência.
 * Retenção especialmente: `metricas.md` §4 diz que ela é leitura de coorte, nunca um número
 * único do dia, e que `R(d,1)` no dado real daria ~100% todo dia.
 */
export function HistoricoPage() {
  const { workspace } = useWorkspace();
  const [periodo, setPeriodo] = useState<PeriodoId>('D30');
  useDocumentTitle(`Histórico · ${workspace.name}`);

  const hourly = useAnalyticsHourly(workspace.id, periodo);
  const activity = useAnalyticsActivity(workspace.id);
  const cohorts = useAnalyticsCohorts(workspace.id, periodo);

  const semMedicao = hourly.data?.observed.firstDay === null;

  return (
    <>
      <PageHeader
        title="Histórico"
        description="A que horas as pessoas entram e saem, como o ano se moveu, e quanto cada turma de entrada permaneceu."
      />

      <PeriodFilter value={periodo} onChange={setPeriodo} timezone={workspace.timezone} />

      {semMedicao ? (
        <FormAlert tone="info" className="mb-3">
          Ainda não há nenhum evento registrado neste workspace. As três leituras abaixo continuam
          visíveis para você saber o que vai aparecer aqui — vazio com explicação, e não zero.
        </FormAlert>
      ) : null}

      <div className="grid grid-cols-1 gap-3 xl:grid-cols-2">
        <HoraCard
          titulo="Entradas por hora"
          descricao="Em que horário as pessoas entram."
          valores={hourly.data?.buckets.map((b) => b.joins)}
          cor={SERIE.entradas.color}
          carregando={hourly.isPending}
          erro={hourly.isError ? hourly.error.message : null}
          onRetry={() => void hourly.refetch()}
          semMedicao={semMedicao === true}
        />
        <HoraCard
          titulo="Saídas por hora"
          descricao="Na amostra real, os picos de saída ficaram em 07h, 09h e 10h."
          valores={hourly.data?.buckets.map((b) => b.leaves)}
          cor={SERIE.saidas.color}
          carregando={hourly.isPending}
          erro={hourly.isError ? hourly.error.message : null}
          onRetry={() => void hourly.refetch()}
          semMedicao={semMedicao === true}
        />
      </div>

      <div className="mt-3">
        {activity.isError ? (
          <ErrorState
            title="Não foi possível carregar a atividade do ano"
            description={activity.error.message}
            onRetry={() => void activity.refetch()}
          />
        ) : activity.isPending || !activity.data ? (
          <Card padded={false} className="p-3 md:p-4">
            <CardTitle>Atividade do ano</CardTitle>
            <div className="mt-3 h-48 animate-pulse rounded-md bg-muted" />
          </Card>
        ) : (
          <ChartCard
            title="Atividade do ano"
            description="Um quadrinho por dia dos últimos 12 meses: quanto o grupo se mexeu."
            legend={<RampLegend peak={activity.data.peak?.intensity ?? null} />}
            empty={
              activity.data.peak === null ? (
                <EmptyState
                  kind="no-data"
                  icon={CalendarDays}
                  title="Nenhum dia com movimentação ainda"
                  description="Assim que houver entrada ou saída, cada dia vira um quadrinho aqui — e a intensidade é comparada com os outros dias do seu próprio ano, não com um máximo fixo."
                />
              ) : undefined
            }
            table={
              <Table caption="Dias mais movimentados do ano" density="dense">
                <TableHeader>
                  <TableRow>
                    <TableHead>Dia</TableHead>
                    <TableHead>Movimentações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...activity.data.days]
                    .filter((d) => d.intensity > 0)
                    .sort((a, b) => b.intensity - a.intensity)
                    .slice(0, 15)
                    .map((d) => (
                      <TableRow key={d.date}>
                        <TableRowHeader>{diaLongo(d.date)}</TableRowHeader>
                        <TableCell numeric>{formatNumber(d.intensity)}</TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            }
          >
            <ActivityGrid cells={activity.data.days} />
          </ChartCard>
        )}
      </div>

      <div className="mt-3">
        {cohorts.isError ? (
          <ErrorState
            title="Não foi possível carregar a retenção"
            description={cohorts.error.message}
            onRetry={() => void cohorts.refetch()}
          />
        ) : (
          <CoorteCard data={cohorts.data} carregando={cohorts.isPending} />
        )}
      </div>

      <div className="mt-3">
        <EvasaoPorOrigem periodo={periodo} />
      </div>
    </>
  );
}

/* ========================================================================== */
/*  Histograma por hora                                                        */
/* ========================================================================== */

/**
 * Uma distribuição por hora — **uma série só, uma cor só**.
 *
 * Entradas e saídas saem em dois cartões irmãos em vez de duas séries no mesmo gráfico: 24
 * faixas com duas barras cada viram 48 barras de 4px em 390px, que é ilegível justamente no
 * tamanho de tela em que o dono abre o painel. Dois gráficos pequenos com o mesmo eixo dizem
 * a mesma coisa e cabem.
 *
 * Sem legenda de propósito: uma série só, e o título já diz o que está plotado. Uma caixa de
 * legenda com um quadradinho repetiria o título e ocuparia espaço.
 */
function HoraCard({
  titulo,
  descricao,
  valores,
  cor,
  carregando,
  erro,
  onRetry,
  semMedicao,
}: {
  titulo: string;
  descricao: string;
  valores: number[] | undefined;
  cor: string;
  carregando: boolean;
  erro: string | null;
  onRetry: () => void;
  semMedicao: boolean;
}) {
  if (erro) {
    return (
      <ErrorState
        title={`Não foi possível carregar: ${titulo}`}
        description={erro}
        onRetry={onRetry}
      />
    );
  }
  if (carregando || !valores) {
    return (
      <Card padded={false} className="p-3 md:p-4">
        <CardTitle>{titulo}</CardTitle>
        <div className="mt-3 h-37.5 animate-pulse rounded-md bg-muted" />
      </Card>
    );
  }

  const total = valores.reduce((a, b) => a + b, 0);
  const maximo = Math.max(...valores);
  // Rótulo direto SÓ no pico — um número em cada uma das 24 colunas é ruído que ninguém lê.
  const pico = maximo > 0 ? [valores.indexOf(maximo)] : [];

  return (
    <ChartCard
      title={titulo}
      description={descricao}
      empty={
        total === 0 ? (
          <EmptyState
            kind={semMedicao ? 'no-data' : 'zero'}
            icon={Clock}
            title={semMedicao ? 'Ainda não houve medição' : 'Nenhum registro no período'}
            description={
              semMedicao
                ? 'Nenhum evento chegou a este workspace, então não há hora nenhuma para distribuir.'
                : 'O período foi medido e não houve nenhum registro neste recorte. Isso é um zero de verdade, não ausência de dado.'
            }
          />
        ) : undefined
      }
      table={
        <Table caption={titulo} density="dense">
          <TableHeader>
            <TableRow>
              <TableHead>Hora</TableHead>
              <TableHead>Quantidade</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {valores.map((v, i) => (
              <TableRow key={i}>
                <TableRowHeader>{horaCurta(i)}</TableRowHeader>
                <TableCell numeric>{formatNumber(v)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      }
    >
      <ColumnChart
        labels={valores.map((_, i) => horaCurta(i))}
        values={valores}
        color={cor}
        highlight={pico}
        ariaLabel={`${titulo}, distribuição nas 24 horas do dia`}
      />
    </ChartCard>
  );
}

/* ========================================================================== */
/*  Retenção por coorte — o mapa da Curva de Evasão (ADR-0019)                 */
/* ========================================================================== */

/**
 * O mapa de coortes: linha = dia de entrada, coluna = D0…D90, célula = **evasão** (quem saiu
 * por conta própria até o fim do dia d+n). A faixa (dentro do normal / acima / mais que o
 * dobro) vem do servidor, contra a mediana de 28 dias do workspace.
 *
 * A célula hachurada com "—" é o ponto desta tabela: o marco ainda não venceu. Pintar `0%`
 * ali desenharia uma diagonal que é só o calendário.
 */
function CoorteCard({
  data,
  carregando,
}: {
  data: AnalyticsCohorts | undefined;
  carregando: boolean;
}) {
  if (carregando || !data) {
    return (
      <Card padded={false} className="p-3 md:p-4">
        <CardTitle>Evasão por turma de entrada</CardTitle>
        <div className="mt-3">
          <TableSkeleton rows={6} columns={6} />
        </div>
      </Card>
    );
  }

  if (data.rows.length === 0) {
    return (
      <Card padded={false} className="p-3 md:p-4">
        <CardTitle>Evasão por turma de entrada</CardTitle>
        <EmptyState
          kind="no-data"
          icon={Users}
          title="Nenhuma entrada registrada no período"
          description="Assim que alguém entrar, mostramos aqui quantos saem com o tempo. Quem já estava no grupo antes do monitorador não forma turma: não sabemos quando entrou."
          compact
        />
      </Card>
    );
  }

  return (
    <Card padded={false} className="p-3 md:p-4">
      <CardTitle>Evasão por turma de entrada</CardTitle>
      <p className="mt-1 text-sm text-muted-foreground">
        Cada linha é quem entrou naquele dia; cada coluna, quantos já tinham saído{' '}
        <strong>por conta própria</strong> até o fim daquele marco. Quem você removeu fica fora. A
        célula com <span aria-hidden="true">—</span> é marco que <strong>ainda não venceu</strong>.
      </p>

      <CohortHeatmap
        caption="Evasão por turma de entrada"
        className="mt-3"
        milestones={data.marcos}
        rows={data.rows.map((row) => ({
          key: row.date,
          label: diaLongo(row.date),
          size: row.size,
          cells: data.marcos.map((n) => ({ value: row.evasao[n], band: row.faixa[n] })),
        }))}
      />

      <p className="mt-3 border-t border-border pt-2 text-xs text-muted-foreground">
        Comparado com o normal dos últimos 28 dias: acima de 1,3× é atenção; acima de 2×, fora do
        normal.
      </p>
    </Card>
  );
}
